import IconTrash from '@apollo/icons/default/IconTrash.svg';
import { Checkbox, IconButton, Input } from '@apollo/orbit';
import classnames from 'classnames';
import { LocationDescriptor } from 'history';
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';

import { HeaderEntry } from 'src/app/graph/explorerPage/hooks/useExplorerState/useHeadersManagerContext/shared';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { WrapMaybe } from 'src/components/wrapMaybe/WrapMaybe';

import { HeaderSuggestionInput } from '../headerSuggestionInput/HeaderSuggestionInput';

export interface Props {
  item: HeaderEntry;
  withCheckbox: boolean;
  isDisabled: boolean;
  disabledLink: LocationDescriptor<unknown> | undefined;
  disabledTooltipContent: string | undefined;
  onCheck: (checked: boolean) => void;
  onHeaderNameChanged?: (value: string) => void;
  onHeaderValueChanged?: React.ChangeEventHandler<HTMLInputElement>;
  onTrashClick?: React.MouseEventHandler<HTMLElement>;
  isDeleteDisabled?: boolean;
}

export const HeaderEditor = ({
  item,
  withCheckbox,
  isDisabled,
  disabledLink,
  disabledTooltipContent,
  // These functions only exist for editable headers. If a header
  // isDisabled it is not editable
  onCheck,
  onHeaderNameChanged,
  onHeaderValueChanged,
  onTrashClick,
  isDeleteDisabled,
}: Props) => {
  const valueInputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-center">
      {withCheckbox && (
        <Checkbox
          isChecked={
            item.checked && item.headerName !== '' && item.value !== ''
          }
          className="mr-2 pl-1"
          onChange={(e) => onCheck(e.target.checked)}
          isDisabled={item.headerName === '' || item.value === ''}
        />
      )}

      <WrapMaybe
        element={<Link className="flex-1" to={disabledLink || ''} />}
        on={isDisabled && !!disabledLink}
      >
        <Tooltip
          placement="top"
          label={disabledTooltipContent}
          disabled={!isDisabled}
        >
          <div className="flex flex-1 items-center">
            <HeaderSuggestionInput
              className="ml-0 mr-2 flex-1"
              inputValue={item.headerName}
              checked={(item.checked || !withCheckbox) && !isDisabled}
              disabled={isDisabled}
              onInputValueChange={onHeaderNameChanged}
              changeFocus={() => {
                valueInputRef.current?.focus();
              }}
            />
            <Input
              ref={valueInputRef}
              value={item.value}
              autoComplete="off"
              type="text"
              name="value"
              isDisabled={isDisabled}
              onChange={onHeaderValueChanged}
              placeholder="value"
              className={classnames(
                'w-full flex-1 rounded-lg placeholder:text-placeholder hover:text-placeholder',
                (item.checked || !withCheckbox) && !isDisabled
                  ? 'text-primary'
                  : 'text-secondary',
              )}
            />

            {!isDisabled ? (
              <IconButton
                aria-label="Delete Header"
                className="ml-2"
                type="button"
                variant="destructive"
                isDisabled={isDeleteDisabled}
                onClick={onTrashClick}
              >
                <IconTrash />
              </IconButton>
            ) : (
              <div className="ml-2 w-9" />
            )}
          </div>
        </Tooltip>
      </WrapMaybe>
    </div>
  );
};
